import { useState } from 'react';
import { FaPaperPlane, FaRobot, FaComments, FaTimes, FaMagic } from 'react-icons/fa';
import bookingAssistantAPI from '../services/bookingAssistantAPI';
import { useAiAssistant } from '../context/AiAssistantContext';
import './AiAssistantInlineBar.css';

const PROMPTS = {
  home: [
    'Family-friendly hotel near the beach under ₹5,000',
    'Quiet workcation with fast Wi-Fi and kitchen',
    'Meeting room for 20 people with projector in Bengaluru',
  ],
  search: [
    'Show me pet-friendly stays under ₹4,000',
    'Best rated hotels with pool and breakfast',
    'Cheaper options closer to the city centre',
  ],
};

/** Compact button for property-type row (left of Meeting Rooms) */
export const AiAssistantTriggerButton = ({ onClick, active = false, compact = false }) => (
  <button
    type="button"
    className={`ai-property-type-btn${active ? ' ai-property-type-btn--active' : ''}${compact ? ' ai-property-type-btn--compact' : ''}`}
    onClick={onClick}
    aria-expanded={active}
    aria-label="Open AI Booking Assistant"
  >
    <FaMagic className="ai-property-type-btn__icon" aria-hidden="true" />
    <span>AI Assistant</span>
  </button>
);

const AiAssistantInlineBar = ({
  variant = 'home',
  onAiResults,
  showInsight = false,
  collapsible = true,
  defaultExpanded = false,
  panelOnly = false,
  expanded: controlledExpanded,
  onClose,
}) => {
  const { openWidget } = useAiAssistant();
  const [internalExpanded, setInternalExpanded] = useState(!collapsible || defaultExpanded);
  const expanded = controlledExpanded !== undefined ? controlledExpanded : internalExpanded;
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [insight, setInsight] = useState(null);

  const prompts = PROMPTS[variant] || PROMPTS.home;

  const runQuery = async (text) => {
    const trimmed = String(text || '').trim();
    if (!trimmed || loading) return;

    setError('');
    setLoading(true);

    try {
      const res = await bookingAssistantAPI.chat({
        message: trimmed,
        history: [],
        language: 'en',
        guestKey: bookingAssistantAPI.guestKey(),
      });

      const payload = {
        reply: res.data.reply,
        recommendations: res.data.recommendations || [],
        intent: res.data.intent || {},
        sessionId: res.data.sessionId,
      };

      setInsight(payload);

      if (onAiResults) {
        onAiResults(payload);
      } else if (variant === 'home') {
        openWidget(trimmed);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'AI assistant is temporarily unavailable.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    runQuery(query);
  };

  const setExpanded = (value) => {
    if (controlledExpanded === undefined) {
      setInternalExpanded(value);
    }
    if (!value && onClose) onClose();
  };

  const handleOpen = () => setExpanded(true);

  const handleClose = () => {
    setExpanded(false);
    setError('');
  };

  if (panelOnly && !expanded) return null;

  return (
    <section className={`ai-inline-bar ai-inline-bar--${variant}`} aria-label="AI booking assistant">
      {!panelOnly && collapsible && !expanded ? (
        <button type="button" className="ai-inline-trigger" onClick={handleOpen}>
          <span className="ai-inline-trigger-icon" aria-hidden="true">
            <FaRobot />
          </span>
          <span className="ai-inline-trigger-text">
            <strong>AI Booking Assistant</strong>
            <small>Describe your stay or meeting in plain language — budget, location, amenities</small>
          </span>
          <span className="ai-inline-trigger-cta">
            <FaMagic /> Ask AI
          </span>
        </button>
      ) : expanded ? (
        <div className="ai-inline-card">
          {(collapsible || panelOnly) && (
            <button
              type="button"
              className="ai-inline-close"
              onClick={handleClose}
              aria-label="Close AI assistant"
            >
              <FaTimes />
            </button>
          )}

          <div className="ai-inline-head">
            <span className="ai-inline-icon" aria-hidden="true">
              <FaRobot />
            </span>
            <div>
              <h3>AI Booking Assistant</h3>
              <p>
                Describe what you need in plain language — budget, location, amenities, travel purpose.
                We&apos;ll recommend the best stays or meeting rooms for you.
              </p>
            </div>
          </div>

          <form className="ai-inline-form" onSubmit={handleSubmit}>
            <input
              className="ai-inline-input"
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setError('');
              }}
              placeholder='e.g. "Family-friendly hotel near the beach under ₹5,000"'
              aria-label="Describe your stay or meeting in natural language"
              autoFocus={expanded}
            />
            <button type="submit" className="ai-inline-submit" disabled={loading || !query.trim()}>
              <FaPaperPlane />
              {loading ? 'Thinking…' : 'Ask AI'}
            </button>
          </form>

          <div className="ai-inline-chips">
            {prompts.map((prompt) => (
              <button
                key={prompt}
                type="button"
                className="ai-inline-chip"
                disabled={loading}
                onClick={() => {
                  setQuery(prompt);
                  runQuery(prompt);
                }}
              >
                {prompt}
              </button>
            ))}
          </div>

          {error && (
            <div className="ai-inline-error" role="alert">
              {error}
            </div>
          )}

          {showInsight && insight && (
            <div className="ai-inline-insight">
              <p>{insight.reply}</p>
              <div className="ai-inline-insight-actions">
                <button
                  type="button"
                  className="ai-inline-link-btn ai-inline-link-btn--primary"
                  onClick={() => openWidget(query || insight.intent?.destination || '')}
                >
                  <FaComments /> Continue in chat
                </button>
              </div>
            </div>
          )}
        </div>
      ) : null}
    </section>
  );
};

export default AiAssistantInlineBar;
